import { Injectable } from '@angular/core';
import { CommonService } from '../common/common.service';
import {
  LS_ACCESS_TOKEN,
  LS_APP_TOKEN,
  LS_CURRENT_USER,
  LS_LAST_ACTIVITY,
  LS_TOKEN_SCOPE,
  LS_USER_MENU,
  LS_USER_ROLE,
  LS_REDIRECT_URL,
  LS_USER_ROLE_DEV,
  LS_APP_TOKEN_API,
  LS_USER_DETAIL,
  LS_USER_GROUP,
  LOGIN_TYPE_INTERNAL,
  LOGIN_TYPE_B2C,
  LS_LOGIN_TYPE,
} from 'src/app/constants/app.constant';
import { Observable, throwError, of, switchMap, catchError, from, mergeMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthUtilService } from '../common/auth-util.service';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EndSessionRequest,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { UserRoleList } from 'src/app/api/common/models/local-storage.model';
import {
  GetAccountResponseMenu,
  GetAccountResponseRoleGroup,
  GetAccountResponseUserDetail,
} from 'src/app/api/auth/models/account/get-account-response.model';
// import { MSALGuardConfigFactory } from 'src/app/app.module';
import Swal from 'sweetalert2';
import {
  isIE,
  MSALGuardConfigFactory,
  MSALGuardConfigFactoryB2C,
  MSALInstanceFactory,
  MSALInstanceFactoryB2C,
  b2cPolicies,
  MSALInstanceFactoryB2CLogout,
} from 'src/app/configs/msal.config';
import { AccountService } from 'src/app/api/auth/services/account/account.service';
import { UrlUtilService } from '../common/url-util.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _authenticated: boolean = false;
  constructor(
    private common: CommonService,
    private router: Router,
    private msalService: MsalService,
    private accountService: AccountService,
    private urlUtil: UrlUtilService
  ) {}

  /**
   * Setter & getter for access token
   */

  // eslint-disable-next-line @typescript-eslint/member-ordering
  get accessToken(): string {
    return this.common.getLocalStorage(LS_ACCESS_TOKEN) ?? '';
  }

  set accessToken(token: string) {
    this.common.setLocalStorage(LS_ACCESS_TOKEN, token);
  }

  get appToken(): string {
    return this.common.getLocalStorage(LS_APP_TOKEN) ?? '';
  }

  set appToken(token: string) {
    this.common.setLocalStorage(LS_APP_TOKEN, token);
  }

  get appTokenApi(): string {
    return this.common.getLocalStorage(LS_APP_TOKEN_API) ?? '';
  }

  set appTokenApi(token: string) {
    this.common.setLocalStorage(LS_APP_TOKEN_API, token);
  }

  get currentUser(): string {
    return this.common.getLocalStorage(LS_CURRENT_USER) ?? '';
  }

  set currentUser(currentUser: any) {
    this.common.setLocalStorage(LS_CURRENT_USER, currentUser);
  }

  get userRole(): number[] {
    return this.common.getLocalStorage(LS_USER_ROLE);
  }

  set userRole(userRole: any) {
    this.common.setLocalStorage(LS_USER_ROLE, userRole);
  }

  get userGroup(): GetAccountResponseRoleGroup[] {
    return this.common.getLocalStorage(LS_USER_GROUP);
  }

  set userGroup(userGroup: any) {
    this.common.setLocalStorage(LS_USER_GROUP, userGroup);
  }

  get userMenu(): GetAccountResponseMenu[] {
    return this.common.getLocalStorage(LS_USER_MENU);
  }

  set userMenu(userMenu: any) {
    this.common.setLocalStorage(LS_USER_MENU, userMenu);
  }

  get userDetail(): GetAccountResponseUserDetail {
    return this.common.getLocalStorage(LS_USER_DETAIL);
  }

  set userDetail(userDetail: any) {
    this.common.setLocalStorage(LS_USER_DETAIL, userDetail);
  }

  get tokenScopes(): string[] {
    return this.common.getLocalStorage(LS_TOKEN_SCOPE) ?? '';
  }

  set tokenScopes(tokenScopes: string[]) {
    this.common.setLocalStorage(LS_TOKEN_SCOPE, tokenScopes);
  }

  get loginType(): string {
    return this.common.getLocalStorage(LS_LOGIN_TYPE) ?? '';
  }

  set loginType(loginType: string) {
    this.common.setLocalStorage(LS_LOGIN_TYPE, loginType);
  }

  // For Dev

  get userRoleDev(): string {
    return this.common.getLocalStorage(LS_USER_ROLE_DEV);
  }

  set userRoleDev(userRoleDev: any) {
    this.common.setLocalStorage(LS_USER_ROLE_DEV, userRoleDev);
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    if (environment.requireAuthen) {
      // do something
      return of(true);
    } else {
      this.accessToken =
        'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImtpZCI6IjJaUXBKM1VwYmpBWVhZR2FYRUpsOGxWMFRPSSJ9.eyJhdWQiOiI0YmFjNGY5ZS02ZjgzLTQ0OWItODk0NS1hZmYwNWY3YjFlMGUiLCJpc3MiOiJodHRwczovL2xvZ2luLm1pY3Jvc29mdG9ubGluZS5jb20vNWRiOGJmMGUtODU5Mi00ZWQwLTgyYjItYTZkNGQ3NzkzM2Q0L3YyLjAiLCJpYXQiOjE2NTkwNzgxODMsIm5iZiI6MTY1OTA3ODE4MywiZXhwIjoxNjU5MDgyMDgzLCJuYW1lIjoic2t5ZmxvdyB0ZXN0MDMiLCJub25jZSI6ImI5NGZjZjU0LTdjMGUtNDNhNy1iNzhkLTBhMGY0ZjAyNTlhYSIsIm9pZCI6ImU0ODU1NjllLTkwMmMtNGY0Ni1hZTQ2LTU1NzNiNjQ5NWZlOSIsInByZWZlcnJlZF91c2VybmFtZSI6InNreWZsb3cudGVzdDAzQHNjZy5jb20iLCJyaCI6IjAuQVZZQURyLTRYWktGMEU2Q3NxYlUxM2t6MUo1UHJFdURiNXRFaVVXdjhGOTdIZzVXQURVLiIsInN1YiI6IllmR1NYT2pwaGpFNmVtZk5HenB1eGpUUG1zd0dIZ1A1NDIwSllzbTBsNE0iLCJ0aWQiOiI1ZGI4YmYwZS04NTkyLTRlZDAtODJiMi1hNmQ0ZDc3OTMzZDQiLCJ1dGkiOiI2VHREbGNtbEgwR3RldkY2TFlLSEFBIiwidmVyIjoiMi4wIn0.UHUyt6Se825laJWMeR8z7uIhcwAzN553pmdZINLToHrpgpo0cGXDIOpaHwONwKCnCY4GHf-yX-9y7jVfznkJBv9esBd996oRMz87fWmu0oNEAsXK3KYt-tElip72UusL6zrtplIOvlidh--a5Fu5GE1MXxse4aIMqBFzemJkHyugAmzh5_tw5xfALuc_eEEI1wvtmFmkRl8WMzl8ox3VJLD1SOCQWbr-afvKMKqy7-XnmSaV5e62-mX75juw2OAEgaJ06PSdVv211wWzKaUjsjGNNuznQpb9QzX9Suz4VEmEngfqJf5dTARWLDDe-3JzhHC_sp2prgR2_ccjw9aWeA';
      const currentUser = {
        name: 'Lastname, Firstname',
        userName: 'firstname.lastname@scg.com',
      };
      this.currentUser = currentUser;
      this._authenticated = true;
      return of(true);
    }
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Renew token
    return of(true);
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem(LS_ACCESS_TOKEN);
    localStorage.removeItem(LS_APP_TOKEN);
    localStorage.removeItem(LS_CURRENT_USER);
    localStorage.removeItem(LS_LAST_ACTIVITY);
    localStorage.removeItem(LS_USER_MENU);
    localStorage.removeItem(LS_USER_ROLE);
    localStorage.removeItem(LS_TOKEN_SCOPE);
    localStorage.removeItem(LS_APP_TOKEN_API);
    localStorage.removeItem(LS_REDIRECT_URL);
    localStorage.removeItem(LS_USER_GROUP);

    // Set the authenticated flag to false
    this._authenticated = false;

    // Return the observable
    return of(true);
  }

  async forceSignOut() {
    this.signOut();
    this.msalService.instance = this.getMSALInstanceFactory();
    await this.msalService.instance.initialize();
    await this.msalService.instance
      .logout({
        ...this.getMSALGuardConfigFactory().authRequest,
      } as EndSessionRequest)
      .then(() => {
        this.router.navigate(['sign-in']);
      });
  }

  async forceSignOutPopup() {
    this.signOut();
    this.msalService.instance = this.getMSALInstanceFactory();
    await this.msalService.instance.initialize();
    await this.msalService.instance
      .logoutPopup({
        ...this.getMSALGuardConfigFactory().authRequest,
      } as EndSessionRequest)
      .then(() => {
        // this.router.navigate(['sign-in']);
      });
  }

  async signInMSAL() {
    if (isIE) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Please sign in with supported browser (Google Chrome)',
      });
      return;
    }
    this.msalService.instance = MSALInstanceFactory();
    this.loginType = LOGIN_TYPE_INTERNAL;
    await setTimeout(async () => {
      // this.msalService
      //   .loginPopup({ ...MSALGuardConfigFactory().authRequest } as PopupRequest)
      //   .subscribe({
      //     next: (result) => {
      //       this.handleLoginObservable(result);
      //     },
      //     error: (error) => {
      //       // this.primeToastService.showError('Login Fail', 'br');
      //       Swal.fire({
      //         icon: 'error',
      //         title: 'Error!',
      //         text: 'Login Fail',
      //       });
      //       console.log(error);
      //     },
      //   });
      await this.msalService.instance.initialize();
      await this.msalService.loginRedirect({
        ...MSALGuardConfigFactory().authRequest,
      } as RedirectRequest);
    });
  }

  async signInMSALB2C() {
    if (isIE) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Please sign in with supported browser (Google Chrome)',
      });
      return;
    }
    this.msalService.instance = MSALInstanceFactoryB2C();
    this.loginType = LOGIN_TYPE_B2C;
    await setTimeout(async () => {
      //const b2cMsal = MSALInstanceFactoryB2C();
      //b2cMsal.loginPopup({ ...MSALGuardConfigFactory().authRequest } as PopupRequest)
      // this.msalService
      //   .loginPopup({
      //     ...MSALGuardConfigFactoryB2C().authRequest,
      //   } as PopupRequest)
      //   .subscribe({
      //     next: (result) => {
      //       this.handleLoginObservable(result);
      //     },
      //     error: (error) => {
      //       // this.primeToastService.showError('Login Fail', 'br');
      //       Swal.fire({
      //         icon: 'error',
      //         title: 'Error!',
      //         text: 'Login Fail',
      //       });
      //       console.log(error);
      //     },
      //   });
      await this.msalService.instance.initialize();
      await this.msalService.loginRedirect({
        ...MSALGuardConfigFactoryB2C().authRequest,
      } as RedirectRequest);
      // window.open('https://scgchemb2cdev.b2clogin.com/scgchemb2cdev.onmicrosoft.com/oauth2/v2.0/authorize?p=B2C_1_sing-in_test&client_id=55001d61-138c-4b26-a4f8-1c6da56d6984&nonce=defaultNonce&redirect_uri=https%3A%2F%2Fexpdocblobfrontenddev.z7.web.core.windows.net%2F&scope=openid&response_type=code&prompt=login', '_self')
    });
  }

  async signOutMsal() {
    this.msalService.instance = this.getMSALInstanceFactory();
    await this.msalService.instance.initialize();
    let account = this.msalService.instance.getActiveAccount();
    // console.log('account', account);

    if (account) {
      await this.msalService.logoutRedirect({ account: account });
      // if (!!this.loginType && this.loginType === LOGIN_TYPE_B2C) {
      //   this.msalService.instance = MSALInstanceFactoryB2C();
      //   account = this.msalService.instance.getActiveAccount();
      //   // const logoutRequest = MSALInstanceFactoryB2CLogout();
      //   // let logoutRequest: EndSessionRequest = {
      //   //   ...MSALInstanceFactoryB2CLogout().getConfiguration().auth,
      //   //   account: account,
      //   //   // postLogoutRedirectUri: `${environment.msalConfig.authorityUrlB2C}oauth2/v2.0/logout?post_logout_redirect_uri=${environment.msalConfig.redirectUriB2C}`,
      //   // };
      //   // this.msalService.logoutRedirect(logoutRequest);
      //   this.msalService.logoutRedirect({ account: account });
      //   // console.log(`${MSALInstanceFactoryB2CLogout().getConfiguration().auth.authority}?post_logout_redirect_uri=${environment.msalConfig.redirectUriB2C}&client_id=${environment.msalConfig.clientIdB2C}`);
      //   // debugger;
      //   // window.open(`${MSALInstanceFactoryB2CLogout().getConfiguration().auth.authority}?post_logout_redirect_uri=${environment.msalConfig.redirectUriB2C}&client_id=${environment.msalConfig.clientIdB2C}`);
      // } else {
      //   this.msalService.logoutRedirect({ account: account });
      // }
    }
  }

  async b2cResetPassword() {
    this.msalService.instance = MSALInstanceFactoryB2C(true);
    this.loginType = LOGIN_TYPE_B2C;
    await setTimeout(async () => {
      await this.msalService.instance.initialize();
      await this.msalService.loginRedirect({
        ...MSALGuardConfigFactoryB2C().authRequest,
      } as RedirectRequest);
    });
  }

  getMSALInstanceFactory() {
    let result = MSALInstanceFactory();
    if (!!this.loginType && this.loginType === LOGIN_TYPE_B2C) {
      result = MSALInstanceFactoryB2C();
    }
    return result;
  }

  getMSALGuardConfigFactory() {
    let result = MSALGuardConfigFactory();
    if (!!this.loginType && this.loginType === LOGIN_TYPE_B2C) {
      result = MSALGuardConfigFactoryB2C();
    }
    return result;
  }

  setAuthenticated() {
    this._authenticated = true;
  }

  redirectToLogin(): Observable<boolean> {
    if (environment.manageAuth.redirectToCommon) {
      // const redirectUrl = localStorage.getItem(LS_REDIRECT_URL) ?? '';
      const redirectUrl = this.common.getLocalStorage(LS_REDIRECT_URL) ?? '';
      if (
        redirectUrl === '' &&
        !redirectUrl.includes('sign-in') &&
        !window.location.href.includes('sign-in')
      ) {
        const parsedUrl = new URL(window.location.href);
        const fullUrl = parsedUrl.href;
        // localStorage.setItem(LS_REDIRECT_URL, fullUrl);
        this.common.setLocalStorage(LS_REDIRECT_URL, fullUrl);
        location.href = environment.manageAuth.loginUrl;
        return of(false);
      } else {
        localStorage.removeItem(LS_REDIRECT_URL);
        return of(false);
      }
    } else {
      const defaultToken = environment.manageAuth.defaultToken;
      const defaultAppToken = environment.manageAuth.defaultAppToken;
      if (environment.manageAuth.defaultToken !== '') {
        const newAppToken = this.generateApplicationToken(defaultToken);
        this.accessToken = defaultToken;
        this.appToken = newAppToken;
        this.appTokenApi = defaultAppToken;
        this.userRole = environment.manageAuth.roles;
        this.currentUser = JSON.stringify(environment.manageAuth.defaultUser);
        return of(true);
      }
      return of(false);
    }
  }

  manageLastActivity() {
    if (!this.appToken) {
      this.redirectToLogin();
      return;
    }
    const now = new Date();
    const tokenExpired = AuthUtilService.getTokenExpirationDate(this.appToken);
    if (!!tokenExpired && now.getTime() <= tokenExpired.getTime()) {
      // localStorage.setItem('lastActivity', '' + now.getTime());
      this.common.setLocalStorage(LS_LAST_ACTIVITY, '' + now.getTime());
    }
  }

  forwardToHome() {
    const _this = this;
    var intervalId: any = null;
    var intervalFunction = function () {
      if (!environment.requireAuthen) {
        _this.router.navigate(['/home'], {
          state: { prevPage: _this.router.url },
        });
      } else if (localStorage.getItem(LS_CURRENT_USER)) {
        clearInterval(intervalId);
        _this.router.navigate(['/home'], {
          state: { prevPage: _this.router.url },
        });
      }
    };
    intervalId = setInterval(intervalFunction, 50);
  }

  forwardToLogin() {
    this._authenticated = false;
    this.router.navigateByUrl('/login');
    this.clearStorage();
  }

  clearStorage() {
    localStorage.clear();
  }

  /**
   * Check the authentication status
   */
  check(): Observable<any> {
    // Check if the user is logged in
    if (this._authenticated) {
      // reset flag
      this._authenticated = false;
      // check whether it is redirect from other module or not. if so, redirect back
      // let redirectUrl = localStorage.getItem(LS_REDIRECT_URL) ?? '';
      let redirectUrl = this.common.getLocalStorage(LS_REDIRECT_URL) ?? '';
      if (redirectUrl !== '') {
        localStorage.removeItem(LS_REDIRECT_URL);
        location.href = redirectUrl;
      }
      return of(true);
    }

    return this.checkToken().pipe(
      switchMap((result) => {
        if (!result) {
          return of(false);
        } else {
          // If the access token exists and it didn't expire, sign in using it
          return this.signInUsingToken();
        }
      })
    );
  }
  generateApplicationToken(adAccessToken: string): string {
    return this.refreshTokenExpiryDate(adAccessToken, true);
  }

  refreshTokenExpiryDate(token: string, ignoreVerify: boolean = false): string {
    let newToken = token;
    let expiryDate = new Date();
    expiryDate.setTime(
      expiryDate.getTime() + environment.manageAuth.session * 1000
    );
    console.log('new expired date', expiryDate);

    newToken =
      AuthUtilService.changeExpirationDate(
        newToken,
        expiryDate,
        ignoreVerify
      ) || '';
    return newToken;
  }

  checkToken(): Observable<any> {
    // Check the access token availability
    if (!this.accessToken || !this.appToken) {
      return of(false);
    }

    // Extend azure AD expiry date.
    return this.extendMsalExpiry().pipe(
      switchMap((result) => {
        try {
          // Check the access token expire date
          // console.log(
          //   'AuthUtilService.isTokenExpired(this.appToken)',
          //   AuthUtilService.isTokenExpired(this.appToken)
          // );

          if (AuthUtilService.isTokenExpired(this.appToken)) {
            // check if last expiry is within session expiry --> refresh
            // const lastActivity = localStorage.getItem(LS_LAST_ACTIVITY);
            const lastActivity = this.common.getLocalStorage(LS_LAST_ACTIVITY);
            if (lastActivity) {
              const now = new Date();
              let lastActivityDate = new Date();
              lastActivityDate.setTime(Number(lastActivity));
              lastActivityDate.setTime(
                lastActivityDate.getTime() +
                  environment.manageAuth.session * 1000
              );
              // extend token if last activity + session time > current date
              // console.log(
              //   'compare last activity',
              //   lastActivityDate.getTime(),
              //   now.getTime()
              // );

              if (lastActivityDate.getTime() >= now.getTime()) {
                this.appToken = this.refreshTokenExpiryDate(
                  this.appToken,
                  false
                );
                // return this.msalService
                //   .acquireTokenSilent({
                //     ...MSALGuardConfigFactory().authRequest,
                //     forceRefresh: true,
                //   } as SilentRequest)
                //   .pipe(
                //     switchMap((resultRefresh) => {
                //       if (!!resultRefresh && !!resultRefresh.account) {
                //         this.setUpStorageAfterLogin(resultRefresh);
                //         this.setActiveAccount(resultRefresh.account);
                //         return of(true);
                //       } else {
                //         return of(false);
                //       }
                //     })
                //   );
                return of(true);
              }
            }
            return of(false);
          }
          return of(true);
        } catch (e) {
          console.log(e);
          return of(false);
        }
      }),
      catchError(() => of(false))
    );
  }

  generateAppToken(
    authenticationResult: AuthenticationResult,
    isGenAppToken: boolean = true
  ) {
    const accessToken = authenticationResult.idToken;
    this.setAuthenticated();
    this.accessToken = accessToken;
    if (isGenAppToken) {
      this.appToken = this.generateApplicationToken(accessToken);
    }
  }

  setTokenScope(authenticationResult: AuthenticationResult) {
    // this.tokenScopes = JSON.stringify(authenticationResult.scopes);
    this.tokenScopes = authenticationResult.scopes;
  }

  setCurrentUser(account: AccountInfo) {
    const currentUser = {
      name: account.name,
      userName: account.username,
    };
    this.currentUser = JSON.stringify(currentUser);
  }

  async setActiveAccount(account: AccountInfo) {
    this.msalService.instance = this.getMSALInstanceFactory();
    await this.msalService.instance.initialize();
    await this.msalService.instance.setActiveAccount(account);
  }

  // getUserRole(): UserRoleList {
  //   return JSON.parse(this.userRole);
  // }

  // setUserRole(userRoleList: UserRoleList) {
  //   this.userRole = JSON.stringify(userRoleList);
  // }

  setUpStorageAfterLogin(
    authenticationResult: AuthenticationResult,
    isGenAppToken: boolean = true
  ) {
    this.generateAppToken(authenticationResult, isGenAppToken);
    this.setTokenScope(authenticationResult);
    if (!!authenticationResult.account) {
      this.setCurrentUser(authenticationResult.account);
    }
  }

  extendMsalExpiry(): Observable<any> {
    this.msalService.instance = this.getMSALInstanceFactory();
    return from(this.msalService.instance.initialize()).pipe(mergeMap(() => {
      let activeAccount = this.msalService.instance.getActiveAccount();
      if (
        activeAccount !== null &&
        activeAccount.idTokenClaims &&
        activeAccount.idTokenClaims.exp
      ) {
        // get msal expiry
        let exp = new Date();
        exp.setTime(activeAccount.idTokenClaims.exp * 1000);
        const now = new Date();
        // silent request if expired.
        if (exp.getTime() < now.getTime() && this.tokenScopes) {
          return this.refreshAccessToken();
        }
      }
      return of(false);
    }));
  }

  refreshAccessToken(): Observable<any> {
    if (!!this.tokenScopes) {
      const silentRequest: SilentRequest = {
        // scopes: JSON.parse(this.tokenScopes),
        scopes: this.tokenScopes,
        forceRefresh: true,
      };
      this.msalService.instance = this.getMSALInstanceFactory();
      return from(this.msalService.instance.initialize()).pipe(mergeMap(() => {
        return this.msalService.acquireTokenSilent(silentRequest).pipe(
          switchMap((result) => {
            console.log('refresh token', result);
  
            if (!!result.account) {
              this.setActiveAccount(result.account);
            }
            this.setUpStorageAfterLogin(result, false);
            return of(true);
          }),
          catchError(() => of(false))
        );
      }));
    } else {
      return of(false);
    }
  }

  handleLoginObservable(response: AuthenticationResult) {
    if (response != null && response.account != null) {
      this.setUpStorageAfterLogin(response);
    }
    setTimeout(() => {
      if (response != null && response.account != null) {
        let firstName = '';
        let lastName = '';
        if (response.account.name?.indexOf(',') !== -1) {
          firstName = response.account.name?.split(',')[1].trim() ?? '';
          lastName = response.account.name?.split(',')[0].trim() ?? '';
        } else if (response.account.name?.indexOf(' ') !== -1) {
          firstName = response.account.name?.split(' ')[1].trim() ?? '';
          lastName = response.account.name?.split(' ')[0].trim() ?? '';
        } else {
          firstName = response.account.name!;
        }
        this.accountService
          .getAccount({
            email: response.account.username,
            firstName: firstName,
            lastName: lastName,
            userId: response.account.username,
          })
          .subscribe(
            (result) => {
              if (
                result.groups.length === 0 ||
                result.roles.length === 0 ||
                result.menu.length === 0
              ) {
                this.signOut();
                Swal.fire({
                  icon: 'error',
                  title: 'Unauthorized',
                  html: 'No Authorize, Please contact <br>Sales Representative or Customer Service.',
                });
                return;
              } else {
                this.appTokenApi = result.appToken;
                this.userMenu = result.menu;
                this.userRole = result.roles;
                this.userDetail = result.userDetail;
                this.userGroup = result.groups;
                setTimeout(() => {
                  if (response.account != null) {
                    this.manageLastActivity();
                    this.manageMsalLogin(response.account);
                  }
                }, 100);
              }
            },
            (error) => {
              this.signOut();
            }
          );
      }
    });
  }

  manageMsalLogin(account: AccountInfo) {
    // Does not work with hash within url
    var currentURLwithoutHash = window.location.href.replace('/#/', '/');
    var searchUrl = new URLSearchParams(new URL(currentURLwithoutHash).search);
    const redirectURL = searchUrl.get(LS_REDIRECT_URL) || '/home';

    this.setActiveAccount(account);

    // Navigate to the redirect url
    this.router.navigateByUrl(redirectURL);
  }
}
